import { ArrowRight, CheckCircle2, Volume2, VolumeX } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { buildActivityOperationPath } from "@/modules/dashboard/payments-activity/utils/activityLinks";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaSuccessMark } from "@/shared/components/motion/ColocaSuccessMark";
import { useDashboardAlertPreferences } from "@/shared/dashboard-alerts/dashboardAlertPreferences";
import { useUnlockDashboardAlertSound } from "@/shared/dashboard-alerts/dashboardAlertPresenter";
import { playDashboardAlertSound } from "@/shared/dashboard-alerts/dashboardAlertSounds";
import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";

import type { BrebKeyIncomingPayment } from "./brebKeyIncomingPayments";

const formatCop = (value: string, locale: string) => `${formatCurrencyAmount(value, "COP", locale)} COP`;

const formatTime = (iso: string, locale: string) =>
  new Date(iso).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });

export interface BrebKeyPaymentsReceivedProps {
  /** Newest first, as `useBrebKeyIncomingPayments` returns them. */
  payments: readonly BrebKeyIncomingPayment[];
  /**
   * How many arrived since the QR opened. Each increase after mount rings and brings the notice into view, so
   * a payment polled in behind a newer one is heard too. Left out, nothing rings.
   */
  arrivals?: number;
  className?: string;
}

/**
 * What arrived through the QR on screen: the newest payment as the one loud moment (how much, from whom, and
 * the way to its transaction), the earlier ones as quiet rows under it. Nothing until a payment arrives.
 */
export function BrebKeyPaymentsReceived({ payments, arrivals = 0, className }: BrebKeyPaymentsReceivedProps) {
  const { t, activeLanguage } = useTranslations("payins");
  const latestRef = useRef<HTMLElement>(null);
  const reduced = usePrefersReducedMotion();
  const { playsSound } = useDashboardAlertPreferences();
  const soundEnabled = playsSound("payins_breb_key");
  const [latest, ...earlier] = payments;
  // What had already arrived when this mounted is not news: only what arrives afterwards rings.
  const heardRef = useRef(arrivals);

  useEffect(() => {
    if (arrivals <= heardRef.current) return;
    heardRef.current = arrivals;
    // On a phone the QR fills the panel and the browser keeps it anchored, so a notice inserted above it
    // would land off screen. Brought into view once per arrival; when it is already visible nothing moves.
    latestRef.current?.scrollIntoView({ block: "nearest", behavior: reduced ? "auto" : "smooth" });
    // For whoever is near the screen but not looking at it. A hidden tab leaves it to the corner alert, which
    // rings there instead, so it is heard once.
    if (soundEnabled && document.visibilityState !== "hidden") playDashboardAlertSound("payin");
  }, [arrivals, reduced, soundEnabled]);

  if (!latest) return null;

  const meta = [
    latest.payerName ? t("breb_key_payment_received_from", { name: latest.payerName }) : null,
    latest.payerBankName,
    formatTime(latest.receivedAt, activeLanguage),
  ].filter(Boolean);
  const credited =
    latest.netAmount && latest.netAmount !== latest.grossAmount
      ? t("breb_key_payment_received_credited", { amount: formatCop(latest.netAmount, activeLanguage) })
      : null;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Keyed by payment, so every new arrival lands with its own mark. */}
      <section
        key={latest.id}
        ref={latestRef}
        aria-label={t("breb_key_payment_received_title")}
        className="rounded-2xl border border-success/30 bg-success-container p-4"
      >
        <div className="flex items-start gap-3">
          <ColocaSuccessMark className="size-10 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-success-container-foreground">
              {t("breb_key_payment_received_title")}
            </p>
            <p className="font-mono text-2xl font-bold tracking-tight text-success-container-foreground tabular-nums">
              {formatCop(latest.grossAmount, activeLanguage)}
            </p>
            {meta.length > 0 && (
              <p className="mt-0.5 text-xs break-words text-success-container-foreground/80">
                {meta.join(" · ")}
              </p>
            )}
            {credited && <p className="mt-1 text-xs text-success-container-foreground/80">{credited}</p>}
          </div>
        </div>
        <ColocaButton
          size="sm"
          iconTrailing={ArrowRight}
          render={<Link to={buildActivityOperationPath("payins", latest.id)} />}
          className="mt-3 w-full sm:w-auto"
        >
          {t("breb_key_payment_received_view")}
        </ColocaButton>
      </section>

      {earlier.length > 0 && (
        <section
          aria-label={t("breb_key_payment_received_earlier")}
          className="rounded-xl border border-border bg-card"
        >
          <p className="px-3 pt-2.5 text-xs font-medium text-muted-foreground">
            {t("breb_key_payment_received_earlier")}
          </p>
          <ul className="divide-y divide-border">
            {earlier.map((payment) => (
              <li key={payment.id} className="flex items-center gap-2.5 px-3 py-2">
                <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden="true" />
                <span className="font-mono text-sm font-semibold text-foreground tabular-nums">
                  {formatCop(payment.grossAmount, activeLanguage)}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {[formatTime(payment.receivedAt, activeLanguage), payment.payerName]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <Link
                  to={buildActivityOperationPath("payins", payment.id)}
                  className="shrink-0 rounded-sm text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  {t("breb_key_payment_received_view_short")}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export interface BrebKeyListeningNoteProps {
  /** Whether a payment already arrived, which changes what the note promises. */
  hasPayments: boolean;
  className?: string;
}

/**
 * Says, under the QR, that this screen will tell when money arrives, with the switch for its sound. Turning
 * the sound on plays it once, so whoever set it knows what to listen for.
 */
export function BrebKeyListeningNote({ hasPayments, className }: BrebKeyListeningNoteProps) {
  const { t } = useTranslations("payins");
  // The sound of "Pagos a tus llaves Bre-B" in Settings → Avisos en el dashboard, at hand where the QR is shown.
  const { sound, modeOf, playsSound, setSound, setMode } = useDashboardAlertPreferences();
  const enabled = playsSound("payins_breb_key");
  useUnlockDashboardAlertSound();

  const soundLabel = t(enabled ? "breb_key_payment_sound_mute" : "breb_key_payment_sound_unmute");

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2 text-center text-xs text-muted-foreground",
        className,
      )}
    >
      <span className="relative flex size-2 shrink-0" aria-hidden="true">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60 motion-reduce:animate-none" />
        <span className="relative inline-flex size-2 rounded-full bg-success" />
      </span>
      <p>{t(hasPayments ? "breb_key_payment_listening_more" : "breb_key_payment_listening")}</p>
      <ColocaIconButton
        size="xs"
        variant="ghost"
        icon={enabled ? Volume2 : VolumeX}
        aria-label={soundLabel}
        title={soundLabel}
        onClick={() => {
          if (enabled) {
            setMode("payins_breb_key", modeOf("payins_breb_key") === "off" ? "off" : "silent");
            return;
          }
          // Back on: the alert with its sound, and the master switch too if it was the one keeping it quiet.
          playDashboardAlertSound("payin", { force: true });
          setMode("payins_breb_key", "sound");
          if (!sound) setSound(true);
        }}
        className="text-muted-foreground"
      />
    </div>
  );
}
